import { AlertsAppliedFilters } from "../models/Dashboard/AlertsDashboard/AlertsDashboardFilters";
import {
  CommanderActionContext,
  trackCommanderAction,
} from "./commanderTracking";
import { AlertsDashboardActionId } from "./catalog/alertsDashboardActions";

/** GA4 custom parameter string values are truncated around 100 chars. */
const GA_PARAM_STRING_MAX = 100;
const GA_EVENT_NAME_MAX = 40;

const AUDIENCE_LABELS: Record<string, string> = {
  ADMIN: "Admin",
  CUSTOMER: "Customer",
};

const VIEW_LABELS: Record<string, string> = {
  drilldown: "Drill-down",
  emails: "Emails",
  push: "Push",
  recipient: "Recipient",
  alert: "Alert detail",
  email: "Email detail",
};

/** Short event-name part per action; the rest use the action id as-is. */
const EVENT_BASE: Partial<Record<AlertsDashboardActionId, string>> = {
  dashboard_view: "view",
  filter_apply: "filters_apply",
  filter_clear: "filters_clear",
  dashboard_refresh: "refresh",
  date_range_change: "custom_dates",
  search_panel_toggle: "filters_panel",
  card_list_click: "card_list",
  trend_day_click: "trend_day",
  type_row_click: "type_row",
  customer_row_click: "customer_row",
  list_status_change: "list_status",
  detail_tab_change: "detail_tab",
  table_page_change: "page",
  table_sort: "sort",
  export_csv: "export",
};

/**
 * Human label for the audience filter.
 *
 * @param audience - ADMIN / CUSTOMER / null.
 * @returns Readable audience label.
 */
export function alertsAudienceLabel(
  audience: string | null | undefined,
): string {
  if (!audience) return "All";
  return AUDIENCE_LABELS[audience] || audience;
}

/**
 * Human label for a drawer view kind.
 *
 * @param view - Drawer view kind.
 * @returns Readable view label.
 */
export function alertsViewLabel(view: string | null | undefined): string {
  if (!view) return "Drawer";
  return VIEW_LABELS[view] || view;
}

/**
 * Short filter brief for action_description (fits GA's ~100 char limit).
 *
 * @param filters - Applied alerts filters.
 * @returns Compact date + audience + filter counts string.
 */
export function alertsFilterBrief(filters: AlertsAppliedFilters): string {
  const counts = [
    filters.section_codes.length,
    filters.type_codes.length,
    filters.severities.length,
    filters.customer_ids.length,
    filters.statuses.length,
  ].reduce((sum, value) => sum + (value > 0 ? 1 : 0), 0);
  return `${filters.start_date.format("YYYY-MM-DD")}→${filters.end_date.format("YYYY-MM-DD")} · ${alertsAudienceLabel(filters.audience)} · ${counts} filters`;
}

/**
 * Shared slim context for every Alerts dashboard GA event.
 *
 * @param filters - Applied alerts filters.
 * @returns Context params merged into the GA event.
 */
export function alertsDashboardFilterContext(
  filters: AlertsAppliedFilters,
): CommanderActionContext {
  return {
    screen_name: `Alerts Dashboard › ${alertsAudienceLabel(filters.audience)}`,
    audience: filters.audience || "ALL",
    start_date: filters.start_date.format("YYYY-MM-DD"),
    end_date: filters.end_date.format("YYYY-MM-DD"),
    section_count: filters.section_codes.length,
    type_count: filters.type_codes.length,
    severity_count: filters.severities.length,
    customer_count: filters.customer_ids.length,
    status_count: filters.statuses.length,
  };
}

/**
 * Caps a description to GA's practical string limit.
 *
 * @param text - Full description.
 * @returns Truncated description.
 */
function clipDescription(text: string): string {
  return text.slice(0, GA_PARAM_STRING_MAX);
}

/**
 * Makes a safe GA event-name fragment.
 *
 * @param value - Raw fragment.
 * @returns Lowercase `[a-z0-9_]` fragment.
 */
function eventPart(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9_]/g, "_")
    .replace(/_+/g, "_")
    .replace(/^_+|_+$/g, "");
}

export interface AlertsDashboardTrackExtra {
  /** Drawer view kind (drilldown / emails / push / recipient / alert / email). */
  view?: string | null;
  /** Card / row / view title the user acted on. */
  title?: string;
  metricId?: string;
  tab?: string;
  preset?: string;
  expanded?: boolean;
  sortColumn?: string;
  sortDir?: string;
  page?: number;
  pageSize?: number;
  statuses?: string[];
  /** Extra raw context fields merged into the event. */
  context?: CommanderActionContext;
}

/**
 * Build dynamic action_name + action_description for GA (overrides catalog defaults).
 *
 * @param actionId - Stable action id.
 * @param filters - Filters active for this action.
 * @param extra - Optional action-specific label inputs.
 * @returns Override labels for trackCommanderAction.
 */
export function alertsDashboardActionLabels(
  actionId: AlertsDashboardActionId,
  filters: AlertsAppliedFilters,
  extra?: AlertsDashboardTrackExtra,
): Pick<CommanderActionContext, "action_name" | "action_description"> {
  const brief = alertsFilterBrief(filters);
  const title = extra?.title?.trim() || alertsViewLabel(extra?.view);

  switch (actionId) {
    case "dashboard_view":
      return {
        action_name: "Opened Alerts dashboard",
        action_description: clipDescription(`Loaded Alerts summary. ${brief}`),
      };
    case "filter_apply":
      return {
        action_name: "Applied alerts filters",
        action_description: clipDescription(`Applied filters. ${brief}`),
      };
    case "filter_clear":
      return {
        action_name: "Cleared alerts filters",
        action_description: clipDescription(`Reset filters. ${brief}`),
      };
    case "tab_change":
      return {
        action_name: `Switched to ${extra?.tab || "tab"}`,
        action_description: clipDescription(
          `Opened the ${extra?.tab || ""} tab. ${brief}`,
        ),
      };
    case "card_click":
    case "card_list_click":
    case "trend_day_click":
    case "type_row_click":
    case "customer_row_click":
    case "alert_open":
    case "email_open":
    case "recipient_open":
      return {
        action_name: `Opened ${title}`.slice(0, GA_PARAM_STRING_MAX),
        action_description: clipDescription(
          `Opened ${alertsViewLabel(extra?.view)} "${title}". ${brief}`,
        ),
      };
    case "list_status_change":
      return {
        action_name: `${title} status → ${(extra?.statuses || []).join(",") || "All"}`,
        action_description: clipDescription(
          `Changed the ${title} list status filter. ${brief}`,
        ),
      };
    case "table_sort":
      return {
        action_name: `Sorted ${title} by ${extra?.sortColumn} ${extra?.sortDir}`,
        action_description: clipDescription(
          `On "${title}": sorted by ${extra?.sortColumn} ${extra?.sortDir}`,
        ),
      };
    case "table_page_change":
      return {
        action_name: `${title} page → ${extra?.page}`,
        action_description: clipDescription(
          `On "${title}": went to page ${extra?.page} (size ${extra?.pageSize})`,
        ),
      };
    case "export_csv":
      return {
        action_name: `Exported CSV: ${title}`,
        action_description: clipDescription(
          `Exported CSV for "${title}". ${brief}`,
        ),
      };
    default:
      return {
        action_name: undefined,
        action_description: clipDescription(`${actionId}: ${title}. ${brief}`),
      };
  }
}

/**
 * Preferred helper for Alerts dashboard GA: readable labels + slim filter context.
 * Fires exactly one GA event per call. Event names: `alerts_db_{action}[_{detail}]`.
 *
 * @param actionId - Catalog action id.
 * @param filters - Applied filters for this action.
 * @param extra - Optional label inputs + raw context fields.
 */
export function trackAlertsDashboardAction(
  actionId: AlertsDashboardActionId,
  filters: AlertsAppliedFilters,
  extra?: AlertsDashboardTrackExtra,
): void {
  try {
    const { context: extraContext, ...labelExtra } = extra || {};
    let base = EVENT_BASE[actionId] || actionId;
    if (actionId === "date_preset_select" && labelExtra.preset) {
      base = `preset_${eventPart(labelExtra.preset)}`;
    } else if (actionId === "tab_change" && labelExtra.tab) {
      base = `tab_${eventPart(labelExtra.tab)}`;
    } else if (actionId === "card_click" && labelExtra.metricId) {
      base = `card_${eventPart(labelExtra.metricId)}`;
    } else if (
      labelExtra.view &&
      (actionId === "table_sort" ||
        actionId === "table_page_change" ||
        actionId === "export_csv" ||
        actionId === "drawer_refresh")
    ) {
      base = `${eventPart(labelExtra.view)}_${base}`;
    }

    trackCommanderAction({
      moduleId: "alerts_dashboard",
      actionId,
      eventName: `alerts_db_${base}`.slice(0, GA_EVENT_NAME_MAX),
      context: {
        ...alertsDashboardFilterContext(filters),
        ...alertsDashboardActionLabels(actionId, filters, labelExtra),
        ...(labelExtra.view ? { drawer_view: labelExtra.view } : {}),
        ...(labelExtra.metricId ? { metric_id: labelExtra.metricId } : {}),
        ...(extraContext || {}),
      },
    });
  } catch {
    // never block UI
  }
}
